import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    BoltIcon,
    CubeIcon,
    DocumentDuplicateIcon,
    ExclamationTriangleIcon,
    LightBulbIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    CheckCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {
    fetchCatalogSkus,
    fetchInventorySkuMappings,
    updateInventorySkuMapping,
    selectInventorySkuMappingItems,
    selectInventorySkuMappingListLoading,
} from "../../slices/inventorySkuMappingSlice";

const MARKETPLACE_SUFFIX_RE = /-(FBA|FBM)$/i;

function normalizeRowBarcode(barcode) {
    if (barcode == null || barcode === "") return "";
    const s = String(barcode).trim();
    if (!s || s === "-") return "";
    return s;
}

function normalizeSupplyLookupQuery(value) {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    return normalized.replace(MARKETPLACE_SUFFIX_RE, "");
}

function getSuggestionStatusClasses(status) {
    if (status === "SUNSET") {
        return {
            panel: "border-amber-300 bg-amber-50",
            heading: "text-amber-800",
            button: "border-amber-300 hover:bg-amber-100/50",
            badge: "bg-amber-100 text-amber-900",
            icon: "text-amber-700",
        };
    }

    if (status === "PLANNED") {
        return {
            panel: "border-blue-300 bg-blue-50",
            heading: "text-blue-800",
            button: "border-blue-300 hover:bg-blue-100/50",
            badge: "bg-blue-100 text-blue-800",
            icon: "text-blue-700",
        };
    }

    return {
        panel: "border-[#a8d5a2] bg-[#f6fff0]",
        heading: "text-[#3a7d44]",
        button: "border-[#a8d5a2] hover:bg-green-100/50",
        badge: "bg-green-100 text-green-800",
        icon: "text-[#3a7d44]",
    };
}

const EMPTY_VALUE = "-";

function renderDisplayValue(value) {
    if (value === null || value === undefined || value === "") {
        return EMPTY_VALUE;
    }
    return value;
}

function ChannelBadge({ channel }) {
    if (channel === "AMAZON") {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#e8f0fe] text-[#00385d]">
                Amazon
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#d3f7d34d] text-[#003d00]">
            Shopify
        </span>
    );
}

function getAutoDuplicateFlag(row) {
    const executionSku = String(row?.execution_sku || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");
    const supplySku = String(row?.supply_sku || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");
    if (!executionSku || !supplySku) return false;
    return (
        executionSku === supplySku ||
        executionSku === `${supplySku}-FBA` ||
        executionSku === `${supplySku}-FBM`
    );
}

function getPotentialPackagingFlag(row) {
    const executionSku = String(row?.execution_sku || "").trim().toUpperCase();
    return executionSku.includes("BX-");
}

function FlagIcons({ flags }) {
    return (
        <div className="flex items-center gap-1">
            {flags.missingSupply ? (
                <ExclamationTriangleIcon
                    className="h-3.5 w-3.5"
                    style={{ color: "oklch(0.60 0.22 25)" }}
                    title="Missing Supply SKU"
                    aria-label="Missing Supply SKU"
                />
            ) : null}
            {flags.duplicateExecution ? (
                <DocumentDuplicateIcon
                    className="h-3.5 w-3.5"
                    style={{ color: "oklch(0.72 0.18 75)" }}
                    title="Duplicate Execution SKU"
                    aria-label="Duplicate Execution SKU"
                />
            ) : null}
            {flags.potentialPackaging ? (
                <CubeIcon
                    className="h-3.5 w-3.5 text-blue-500"
                    title="Potential Packaging SKU"
                    aria-label="Potential Packaging SKU"
                />
            ) : null}
            {flags.autoDuplicate ? (
                <BoltIcon
                    className="h-3.5 w-3.5 text-purple-500"
                    title="Auto Duplicate Flag"
                    aria-label="Auto Duplicate Flag"
                />
            ) : null}
        </div>
    );
}

function SupplySkuPopover({ row, anchorRect, onClose, onPicked, loadCatalogSkus }) {
    const [query, setQuery] = useState("");
    const [debounced, setDebounced] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const panelRef = useRef(null);

    const barcodeForFilter = useMemo(
        () => (row.has_supply ? normalizeRowBarcode(row.barcode) : ""),
        [row.barcode, row.has_supply]
    );
    const scopedByBarcode = Boolean(barcodeForFilter);
    const suggestionItem = row.suggested_supply_product_id
        ? {
              id: row.suggested_supply_product_id,
              sku: row.suggested_supply_sku || "",
              status: row.suggested_supply_status || null,
          }
        : null;
    const suggestionStyles = getSuggestionStatusClasses(suggestionItem?.status);

    useEffect(() => {
        setQuery(row.supply_sku || "");
    }, [row.id, row.supply_sku]);

    useEffect(() => {
        const delay = scopedByBarcode ? 0 : 300;
        const t = setTimeout(() => setDebounced(normalizeSupplyLookupQuery(query)), delay);
        return () => clearTimeout(t);
    }, [query, scopedByBarcode]);

    useEffect(() => {
        if (!scopedByBarcode && debounced.length < 1) {
            setItems([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        loadCatalogSkus({
            picker: "supply",
            includeNameMatch: true,
            limit: 50,
            excludeProductId: row.execution_product_id,
            ...(barcodeForFilter ? { barcode: barcodeForFilter } : {}),
            ...(debounced ? { search: debounced } : {}),
        })
            .then((list) => {
                if (!cancelled) setItems(list || []);
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debounced, barcodeForFilter, scopedByBarcode, row.execution_product_id, loadCatalogSkus]);

    useEffect(() => {
        const onDoc = (e) => {
            if (e.target?.closest?.("[data-supply-sku-trigger]")) return;
            if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
        };
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        const t = setTimeout(() => {
            document.addEventListener("mousedown", onDoc);
        }, 0);
        document.addEventListener("keydown", onKey);
        return () => {
            clearTimeout(t);
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    if (!anchorRect) return null;

    const barcodeDisplay = row.barcode || "—";
    const panelMaxPx = Math.min(window.innerWidth - 32, 22 * 16);
    /** Top-left of the panel matches the cell’s top-left (viewport); only nudge left if it would overflow right. */
    const leftPx = Math.min(anchorRect.left, window.innerWidth - panelMaxPx - 16);
    const topPx = anchorRect.top;

    return createPortal(
        <div
            ref={panelRef}
            data-supply-sku-panel
            className="pointer-events-auto fixed z-[200] w-[min(22rem,calc(100vw-2rem))] max-w-none rounded-lg border border-gray-200 bg-white shadow-xl"
            style={{
                left: leftPx,
                top: topPx,
            }}
            role="dialog"
            aria-label="Edit supply SKU"
        >
            <div className="border-b border-gray-100 px-3 pt-2.5 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide mb-1 text-gray-400">Editing row</p>
                <p className="text-xs font-medium text-gray-900">
                    {row.execution_sku}
                    <span className="text-[10px] !font-[300] text-gray-600">
                        {" "}
                        - {barcodeDisplay}
                    </span>
                </p>
            </div>
            {!row.has_supply && suggestionItem ? (
                <div className={`border-b px-3 py-2.5 ${suggestionStyles.panel}`}>
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                        <p className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${suggestionStyles.heading}`}>
                            <LightBulbIcon className="h-3 w-3 shrink-0" aria-hidden />
                            Barcode suggestion
                        </p>

                    </div>
                    <button
                        type="button"
                        className={`flex w-full items-center justify-between rounded bg-transparent px-2.5 py-2 text-left transition-colors ${suggestionStyles.button}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onPicked(suggestionItem)}
                    >
                        <span className="font-mono text-sm font-medium text-gray-900">{suggestionItem.sku}</span>
                        {suggestionItem.status ? (
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${suggestionStyles.badge}`}>
                                {suggestionItem.status === "ACTIVE" ? "Active" : suggestionItem.status === "SUNSET" ? "Sunset" : suggestionItem.status === "PLANNED" ? "Planned" : suggestionItem.status}
                            </span>
                        ) : null}
                    </button>
                </div>
            ) : null}
            <div className="">
                <div className="relative flex items-center gap-2 px-3 py-2 border-b border-border bg-white">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="no-search-input-chrome w-full pl-5 text-sm outline-none"
                        placeholder="Search supply SKU…"
                    />
                    {query ? (
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                            onClick={() => setQuery("")}
                            aria-label="Clear search"
                        >
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    ) : null}
                </div>
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
                {loading ? (
                    <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>
                ) : !scopedByBarcode && debounced.length < 1 ? (
                    <li className="px-3 py-2 text-sm text-gray-500">Type to search supply products.</li>
                ) : items.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500">No matches.</li>
                ) : (
                    items.map((item) => {
                        const selected = item.id === row.supply_product_id;
                        return (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                                        selected ? "bg-gray-100" : ""
                                    }`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => onPicked(item)}
                                >
                                    <span className="min-w-0 flex-1 truncate font-semibold text-gray-900">{item.sku}</span>
                                    <span className="flex shrink-0 items-center gap-2">
                                        {selected ? <span className="text-gray-700" aria-hidden>✓</span> : null}
                                    </span>
                                </button>
                            </li>
                        );
                    })
                )}
            </ul>
        </div>,
        document.body
    );
}

const SkuMappingManualTable = ({
    searchSku,
    selectedChannels,
    unmappedOnly = false,
    refreshKey = 0,
    selectionResetKey = 0,
    onFilteredCountChange,
    onMappingChanged,
    onSelectionChange,
}) => {
    const dispatch = useDispatch();
    const rows = useSelector(selectInventorySkuMappingItems);
    const listLoading = useSelector(selectInventorySkuMappingListLoading);

    const loadCatalogSkus = useCallback(
        (params) => dispatch(fetchCatalogSkus(params)).unwrap(),
        [dispatch]
    );

    const [selected, setSelected] = useState(() => new Set());
    const [supplyAnchor, setSupplyAnchor] = useState(null);
    const [supplyRow, setSupplyRow] = useState(null);
    const [editingNotesId, setEditingNotesId] = useState(null);
    const [draftNotes, setDraftNotes] = useState("");
    const headerCheckboxRef = useRef(null);

    useEffect(() => {
        dispatch(fetchInventorySkuMappings()).then((action) => {
            if (fetchInventorySkuMappings.rejected.match(action)) {
                toast.error("Could not load SKU mappings.", { position: "bottom-right" });
            }
        });
    }, [dispatch, refreshKey]);

    useEffect(() => {
        setSelected(new Set());
        setSupplyAnchor(null);
        setSupplyRow(null);
        setEditingNotesId(null);
    }, [selectionResetKey]);

    const filtered = useMemo(() => {
        let list = rows;
        const q = searchSku.trim().toLowerCase();
        if (q) {
            list = list.filter((r) => {
                const blob = [
                    r.execution_sku,
                    r.supply_sku,
                    r.canonical_product_name,
                    r.notes,
                    r.barcode,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return blob.includes(q);
            });
        }
        const ch = new Set((selectedChannels || []).map((c) => String(c).toLowerCase()));
        if (ch.size > 0 && ch.size < 2) {
            list = list.filter((r) => {
                const key = r.channel === "AMAZON" ? "amazon" : "shopify";
                return ch.has(key);
            });
        }
        if (unmappedOnly) {
            list = list.filter((r) => !r.has_supply);
        }
        return list;
    }, [rows, searchSku, selectedChannels, unmappedOnly]);
    const executionSkuCounts = useMemo(() => {
        const counts = new Map();
        rows.forEach((row) => {
            const key = String(row.execution_sku || "").trim().toUpperCase();
            if (!key) return;
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        return counts;
    }, [rows]);

    useEffect(() => {
        onFilteredCountChange?.(filtered.length);
    }, [filtered.length, onFilteredCountChange]);

    const filteredIds = useMemo(() => filtered.map((r) => r.id), [filtered]);
    const allFilteredSelected =
        filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
    const someFilteredSelected = filteredIds.some((id) => selected.has(id));

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
        }
    }, [someFilteredSelected, allFilteredSelected]);

    useEffect(() => {
        onSelectionChange?.(Array.from(selected));
    }, [selected, onSelectionChange]);

    const toggleAllFiltered = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredIds.forEach((id) => next.delete(id));
            } else {
                filteredIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const toggleOne = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const openSupplyEditor = (e, row) => {
        const td = e.currentTarget.closest("[data-supply-sku-cell]");
        const rect = td ? td.getBoundingClientRect() : e.currentTarget.getBoundingClientRect();
        setSupplyAnchor({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        });
        setSupplyRow(row);
    };

    const closeSupplyEditor = () => {
        setSupplyAnchor(null);
        setSupplyRow(null);
    };

    const saveSupply = async (row, product) => {
        if (!product?.id || product.id === row.supply_product_id) {
            closeSupplyEditor();
            return;
        }
        try {
            await dispatch(
                updateInventorySkuMapping({ id: row.id, body: { supply_product_id: product.id } })
            ).unwrap();
            toast.success("Supply SKU updated.", { position: "bottom-right" });
            closeSupplyEditor();
            await dispatch(fetchInventorySkuMappings());
            onMappingChanged?.();
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Update failed.";
            toast.error(msg, { position: "bottom-right" });
        }
    };

    const acceptSuggestedSupply = async (row) => {
        if (!row.suggested_supply_product_id) return;
        try {
            await dispatch(
                updateInventorySkuMapping({
                    id: row.id,
                    body: { supply_product_id: row.suggested_supply_product_id },
                })
            ).unwrap();
            toast.success("Suggestion accepted.", { position: "bottom-right" });
            await dispatch(fetchInventorySkuMappings());
            onMappingChanged?.();
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Update failed.";
            toast.error(msg, { position: "bottom-right" });
        }
    };

    const startNotesEdit = (row) => {
        setEditingNotesId(row.id);
        setDraftNotes(row.notes || "");
    };

    const commitNotes = async (row) => {
        const next = draftNotes.trim() || null;
        const prev = row.notes ? String(row.notes).trim() : null;
        setEditingNotesId(null);
        if (next === prev) return;
        try {
            await dispatch(updateInventorySkuMapping({ id: row.id, body: { notes: next } })).unwrap();
            toast.success("Notes saved.", { position: "bottom-right" });
            await dispatch(fetchInventorySkuMappings());
            onMappingChanged?.();
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Save failed.";
            toast.error(msg, { position: "bottom-right" });
        }
    };

    const thClass = "px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-200";
    const tdClass = "px-3 py-2 text-xs text-gray-900 border-b border-gray-100 align-middle truncate";

    if (listLoading && rows.length === 0) {
        return (
            <div className="flex items-center justify-center px-4 py-12 text-sm text-gray-500">
                Loading mappings…
            </div>
        );
    }

    return (
        <div className="relative overflow-x-auto border-x border-b border-gray-200 bg-white">
            <table className="min-w-[1100px] w-full border-collapse text-sm">
                <thead className="bg-white">
                    <tr>
                        <th className={`${thClass} w-10 whitespace-nowrap`}>
                            <input
                                type="checkbox"
                                ref={headerCheckboxRef}
                                checked={allFilteredSelected}
                                onChange={toggleAllFiltered}
                                className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                aria-label="Select all rows"
                            />
                        </th>
                        <th className={`${thClass} w-14 whitespace-nowrap`}>Flags</th>
                        <th className={thClass + " whitespace-nowrap"}>Execution SKU</th>
                        <th className={thClass + " w-[300px] truncate whitespace-nowrap"}>Product</th>
                        <th className={`${thClass} w-28 whitespace-nowrap`}>Channel</th>
                        <th className={thClass + " min-w-[130px] whitespace-nowrap"}>Supply SKU</th>
                        <th className={thClass + " whitespace-nowrap"}>Barcode</th>
                        <th className={thClass + " w-[300px] truncate whitespace-nowrap"}>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.length === 0 ? (
                        <tr>
                            <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-500">
                                No manual mappings match the current filters.
                            </td>
                        </tr>
                    ) : (
                        filtered.map((row, index) => {
                            const rowSelected = selected.has(row.id);
                            const executionSkuKey = String(row.execution_sku || "").trim().toUpperCase();
                            const flags = {
                                missingSupply: !row.has_supply,
                                duplicateExecution: (executionSkuCounts.get(executionSkuKey) || 0) > 1,
                                potentialPackaging: getPotentialPackagingFlag(row),
                                autoDuplicate: getAutoDuplicateFlag(row),
                            };
                            const hasFlags =
                                flags.missingSupply ||
                                flags.duplicateExecution ||
                                flags.potentialPackaging ||
                                flags.autoDuplicate;
                            return (
                            <tr
                                key={row.id}
                                className={`catalog-style-row transition-colors duration-75 hover:bg-gray-100/80 ${
                                    rowSelected ? "bg-gray-100" : index % 2 === 1 ? "bg-gray-50/80" : "bg-white"
                                }`}
                            >
                                <td className={tdClass}>
                                    <input
                                        type="checkbox"
                                        checked={rowSelected}
                                        onChange={() => toggleOne(row.id)}
                                        className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-primary focus:ring-primary focus:ring-offset-0"
                                        aria-label={`Select row ${row.execution_sku}`}
                                    />
                                </td>
                                <td className={tdClass}>    
                                    <div className="flex justify-center">
                                        {hasFlags ? (
                                            <FlagIcons flags={flags} />
                                        ) : row.has_supply ? (
                                            <CheckCircleIcon
                                                className="h-4 w-4 text-green-400"
                                                title="Supply connected"
                                            />
                                        ) : (
                                            <ExclamationTriangleIcon
                                                className="h-4 w-4 text-red-600"
                                                title="No supply SKU"
                                            />
                                        )}
                                    </div>
                                </td>
                                <td className={`${tdClass} text-xs font-medium uppercase`}>{row.execution_sku}</td>
                                <td className={tdClass}>
                                    <span
                                        className={`line-clamp-2 break-words text-xs font-medium text-gray-900`}
                                    >
                                        {renderDisplayValue(row.canonical_product_name)}
                                    </span>
                                </td>
                                <td className={tdClass}>
                                    <ChannelBadge channel={row.channel} />
                                </td>
                                <td
                                    data-supply-sku-cell
                                    className={`group ${tdClass} relative`}
                                    style={
                                        supplyRow?.id === row.id && supplyAnchor
                                            ? {
                                                  minHeight: Math.max(supplyAnchor.height || 0, 72),
                                              }
                                            : undefined
                                    }
                                >
                                    {supplyRow?.id === row.id ? (
                                        <div className="w-full min-h-[22px]" aria-hidden />
                                    ) : (
                                        <div className="inline-flex max-w-full items-center gap-1.5">
                                            {row.supply_sku ? (
                                                <>
                                                    <div className="inline-flex min-w-0 max-w-full items-center rounded border border-border bg-white px-[10px] py-0.5 text-xs font-medium">
                                                        <span className="min-w-0 flex-1 truncate font-semibold text-gray-900">
                                                            {row.supply_sku}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        data-supply-sku-trigger
                                                        className="shrink-0 rounded p-0.5 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                                                        aria-label="Change supply SKU"
                                                        onClick={(e) => openSupplyEditor(e, row)}
                                                    >
                                                        <PencilSquareIcon className="h-3 w-3" />
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-start gap-1.5">
                                                    <button
                                                        type="button"
                                                        data-supply-sku-trigger
                                                        className="flex min-h-[22px] items-center gap-1 rounded p-0.5 text-left text-xs italic text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                                        onClick={(e) => openSupplyEditor(e, row)}
                                                    >
                                                        <span>Unmapped</span>
                                                        <ExclamationTriangleIcon
                                                            className="h-4 w-4 shrink-0 text-red-500"
                                                            aria-hidden
                                                        />
                                                    </button>
                                                    {row.suggested_supply_sku ? (
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100"
                                                            onClick={() => acceptSuggestedSupply(row)}
                                                        >
                                                            <LightBulbIcon className="h-3 w-3 shrink-0" aria-hidden />
                                                            <span>{row.suggested_supply_sku}</span>
                                                            <CheckCircleIcon className="h-3 w-3 shrink-0 text-amber-600" aria-hidden />
                                                        </button>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className={tdClass}>{renderDisplayValue(row.barcode)}</td>
                                <td className={tdClass + " max-w-[300px]"}>
                                    {editingNotesId === row.id ? (
                                        <input
                                            type="text"
                                            value={draftNotes}
                                            onChange={(e) => setDraftNotes(e.target.value)}
                                            onBlur={() => commitNotes(row)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") e.target.blur();
                                                if (e.key === "Escape") {
                                                    setEditingNotesId(null);
                                                }
                                            }}
                                            className="w-full rounded-md border border-gray-900 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                                            autoFocus
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className="group flex w-full min-w-0 items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-gray-100"
                                            onClick={() => startNotesEdit(row)}
                                        >
                                            <span className="min-w-0 flex-1 truncate text-gray-900">
                                                {row.notes ? row.notes : (
                                                    <span className="text-gray-400 italic">Add note...</span>
                                                )}
                                            </span>
                                            <PencilSquareIcon className="h-4 w-4 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )})
                    )}
                </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-2">
                <p className="text-xs text-gray-500">
                    {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} rows
                </p>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                        <ExclamationTriangleIcon className="h-3 w-3" style={{ color: "oklch(0.60 0.22 25)" }} />
                        Missing
                    </span>
                    <span className="flex items-center gap-1">
                        <DocumentDuplicateIcon className="h-3 w-3" style={{ color: "oklch(0.72 0.18 75)" }} />
                        Duplicate
                    </span>
                    <span className="flex items-center gap-1">
                        <CubeIcon className="h-3 w-3 text-blue-500" />
                        Packaging
                    </span>
                    <span className="flex items-center gap-1">
                        <BoltIcon className="h-3 w-3 text-purple-500" />
                        Auto-Dup
                    </span>
                </div>
            </div>

            {supplyRow && supplyAnchor ? (
                <SupplySkuPopover
                    row={supplyRow}
                    anchorRect={supplyAnchor}
                    onClose={closeSupplyEditor}
                    onPicked={(item) => saveSupply(supplyRow, item)}
                    loadCatalogSkus={loadCatalogSkus}
                />
            ) : null}
        </div>
    );
};

export default SkuMappingManualTable;
